<template>
    <v-form ref="form" v-model="valid" class="pb-4">
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col class="flex-grow-0">
                <v-text-field
                    v-model="itinerary.name"
                    :label="`${$t('entry_code')}*`"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="entry-code"
                    width="150px"
                    :rules="[requiredRule('entry_code')]"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="6">
                <TextFieldLocale
                    v-model="itinerary.productName[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
        </v-row>
        <v-row v-if="itineraryType === Itinerary.Flight" dense class="pb-4">
            <v-radio-group v-model="itinerary.journeyType" density="compact" hide-details>
                <v-radio :label="$t('journeyType.ONEWAY')" value="ONEWAY" color="primary" data-cy="ONEWAY" />
                <v-radio :label="$t('journeyType.ROUNDTRIP')" value="ROUNDTRIP" color="primary" data-cy="ROUNDTRIP" />
            </v-radio-group>
        </v-row>
        <v-row dense>
            <v-col class="flex-grow-0">
                <v-select
                    v-model="itinerary.dayNumber"
                    :items="useDays"
                    :label="itineraryType === Itinerary.Flight ? `${$t('departure_day')}*` : `${$t('day_number')}*`"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    width="150px"
                    :rules="[requiredRule('day_number')]"
                    data-cy="day-number"
                />
            </v-col>
            <v-col
                v-if="itineraryType === Itinerary.Flight && itinerary.journeyType === 'ROUNDTRIP'"
                class="flex-grow-0"
            >
                <v-select
                    v-model="itinerary.returnDayNumber"
                    :items="useDays"
                    :label="`${$t('return_day')}*`"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    width="150px"
                    :rules="[requiredRule('return_day')]"
                    data-cy="return-day"
                />
            </v-col>
        </v-row>
        <v-row dense class="pb-8">
            <v-col cols="12">
                <div class="text-subtitle-1">{{ $t('include_component') }}*</div>
                <v-radio-group v-model="itinerary.obligatoryStatus" density="compact" hide-details>
                    <v-radio
                        :label="$t('required_component')"
                        value="REQUIRED"
                        color="primary"
                        data-cy="required-components"
                    />
                    <v-radio
                        :label="$t('optional_component')"
                        value="OPTIONAL"
                        color="primary"
                        data-cy="optional-components"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col v-if="itineraryType === Itinerary.Accommodation" cols="12" md="3">
                <CitySearchInput
                    v-if="!isLoading"
                    v-model="itinerary.cityId"
                    product-type="ACCOMMODATION"
                    :label="$t('city')"
                    @update:model-value="changeCity"
                />
            </v-col>
            <v-col cols="12" md="9">
                <v-row dense>
                    <v-col cols="12" md="4">
                        <ProductSearchInput
                            v-if="!isLoading && !useIsShowMultipleTable"
                            v-model="itinerary.productIds[0]"
                            :method="useMethod"
                            :city-id="itinerary.cityId"
                            :label="`${$t('product.product')}*`"
                            :rules="[requiredRule('product.product')]"
                        />
                        <div v-if="useIsShowMultipleTable">
                            <v-data-table
                                :headers="useHeaders"
                                :items="useMultipleProducts"
                                disable-sort
                                density="compact"
                                color="primary"
                                class="elevation-1 table-server mb-2"
                                data-cy="table"
                                :items-per-page="-1"
                                hide-default-footer
                            >
                                <template v-slot:item.action>
                                    <v-btn
                                        variant="text"
                                        color="primary"
                                        class="mr-4 text-caption"
                                        data-cy="delete"
                                        density="compact"
                                        @click="resetProducts()"
                                    >
                                        {{ $t('delete') }}
                                    </v-btn>
                                </template>
                            </v-data-table>
                            <v-btn
                                color="primary"
                                class="mb-6"
                                data-cy="edit"
                                density="compact"
                                @click="isShowHotelModal = true"
                            >
                                {{ $t('edit') }}
                            </v-btn>
                        </div>
                    </v-col>
                    <v-btn
                        v-if="itineraryType === Itinerary.Accommodation && !useIsShowMultipleTable"
                        variant="text"
                        color="primary"
                        class="text-caption my-4"
                        data-cy="add"
                        density="compact"
                        @click="isShowHotelModal = true"
                    >
                        {{ $t('multiple_choice') }}
                    </v-btn>
                </v-row>
                <v-checkbox
                    v-if="itineraryType === Itinerary.Accommodation"
                    v-model="itinerary.externalSuppliersEnables"
                    :label="$t('external_suppliers_enables')"
                    density="compact"
                    class="pb-8"
                    color="primary"
                    hide-details
                    data-cy="external-suppliers-enables"
                />
            </v-col>
        </v-row>
        <v-row v-if="itineraryType === Itinerary.Accommodation || itineraryType === Itinerary.Car" dense>
            <v-col class="flex-grow-0">
                <v-select
                    v-model="itinerary.nightsCount"
                    :items="useNights"
                    :label="`${$t('number_of_nights')}*`"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    width="150px"
                    :rules="[requiredRule('number_of_nights')]"
                    data-cy="number-of-nights"
                />
            </v-col>
        </v-row>
        <v-row v-if="itineraryType === Itinerary.Insurance" dense>
            <v-col class="flex-grow-0">
                <v-select
                    v-model="itinerary.daysCount"
                    :items="useDays"
                    :label="`${$t('number_of_days')}*`"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    width="150px"
                    :rules="[requiredRule('number_of_days')]"
                    data-cy="number-of-days"
                />
            </v-col>
        </v-row>
        <v-row v-if="itineraryType === Itinerary.Flight" dense class="pb-8">
            <v-col cols="12" sm="6" md="3">
                <div class="text-subtitle-1 font-weight-medium">{{ $t('tours.departure') }}</div>
                <airports-search-input
                    v-if="!isLoading"
                    v-model="itinerary.departureAirportCode"
                    :single-line="true"
                    color="primary"
                    variant="outlined"
                    hide-details
                    data-cy="departure-airport-id"
                />
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <div class="text-subtitle-1 font-weight-medium">{{ $t('tours.destination') }}</div>
                <airports-search-input
                    v-if="!isLoading"
                    v-model="itinerary.arrivalAirportCode"
                    :single-line="true"
                    color="primary"
                    variant="outlined"
                    hide-details
                    data-cy="destination-airport-id"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12">
                <div class="text-subtitle-1 font-weight-medium">{{ $t('tags') }}</div>
                <div class="text-grey pb-2 text-subtitle-2 font-weight-regular">{{ $t('tags_note') }}</div>
            </v-col>
            <v-col cols="12" md="6">
                <div class="text-subtitle-1">{{ $t('requested_tags_note') }}:</div>
                <v-textarea
                    v-model="itinerary.requestedTags"
                    variant="outlined"
                    rows="2"
                    color="primary"
                    data-cy="requested-tags"
                />
                <div class="text-subtitle-1">{{ $t('excluded_tags_note') }}:</div>
                <v-textarea
                    v-model="itinerary.excludedTags"
                    variant="outlined"
                    rows="2"
                    color="primary"
                    data-cy="excluded-tags"
                />
            </v-col>
        </v-row>
        <template v-if="itineraryType === Itinerary.Accommodation">
            <InfoHotelPointModal
                v-model="isShowHotelModal"
                :selected-point="{cityId: itinerary.cityId, hotelIds: itinerary.productIds}"
                :title="$t('choose_products')"
                :subtitle="$t('choose_products_note')"
                @save="saveSelectedPoints($event)"
            />
        </template>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid || isSaving" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading || isSaving" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {initItinerary} from '~/utils/types/account/products/helpers'
    import {Itinerary} from '~/utils/types/types'
    import AirportsSearchInput from '~/components/snippets/AirportsSearchInput.vue'
    import CitySearchInput from '~/components/snippets/CitySearchInput.vue'
    import ProductSearchInput from '~/components/snippets/ProductSearchInput.vue'
    import InfoHotelPointModal from '~/components/products/information/InfoHotelPointModal.vue'
    import {useFullContract} from '~/composables/form-helpers'

    const props = defineProps<{
        contractId: number
        editItineraryId?: number | null
        selectedItinerary?: string | null
        entryCodeCount: number
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const isLoading = ref<boolean>(false)
    const isSaving = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const itinerary = ref(initItinerary())
    const itineraryType = ref(props.selectedItinerary)
    const entryCode = {
        ACCOMMODATION: 'H',
        TRANSFER: 'T',
        FLIGHT: 'F',
        EXCURSION: 'A',
        INSURANCE: 'I',
        EVENT: 'Ev',
        EXTRA_SERVICE: 'Ex',
        CAR: 'Car',
        CRUISE: 'Cru',
    }
    const isShowHotelModal = ref<boolean>(false)
    const multipleProducts = ref([])
    const form = ref()
    const duration = ref(useFullContract.value.duration || 1)
    const ownAccommodationContracts = ref([])

    watch(useFullContract, async () => {
        duration.value = useFullContract.value.duration
        itinerary.value.dayNumber = 1
        if (props.selectedItinerary === Itinerary.Insurance) {
            itinerary.value.daysCount = null
        }
        if (props.selectedItinerary === Itinerary.Accommodation || props.selectedItinerary === Itinerary.Car) {
            itinerary.value.nightsCount = null
        }
    })

    const changeCity = () => {
        itinerary.value.productIds = []
        multipleProducts.value = []
        form.value.validate()
    }

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        if (props.editItineraryId) {
            await loadData()
            itineraryType.value = itinerary.value?.type
        }
        if (!props.editItineraryId) {
            itineraryType.value = props.selectedItinerary
            itinerary.value.productName[activeLangCode.value] = t(`itineraryType.${props.selectedItinerary}`)
            itinerary.value.name = `${entryCode[props.selectedItinerary]}${props.entryCodeCount}`
            itinerary.value.type = props.selectedItinerary
            if (props.selectedItinerary === Itinerary.Flight) {
                itinerary.value.journeyType = 'ONEWAY'
                itinerary.value.returnDayNumber = 1
            }
            if (props.selectedItinerary === Itinerary.Accommodation || props.selectedItinerary === Itinerary.Car) {
                itinerary.value.nightsCount = null
            }
            if (props.selectedItinerary === Itinerary.Insurance) {
                itinerary.value.daysCount = 1
            }
        }
        if (props.selectedItinerary === Itinerary.Accommodation && props.editItineraryId) {
            await loadAccommodationContracts()

            if (itinerary.value.productIds.length > 1) {
                multipleProducts.value = ownAccommodationContracts.value.filter(({id}) =>
                    itinerary.value.productIds.includes(id)
                )
            }
        }
    })

    async function loadAccommodationContracts(): Promise<void> {
        try {
            const rq = {cityId: itinerary.value?.cityId}
            const {ownProductContracts} = await app.$api.ownAccommodationContracts.get(rq)
            ownAccommodationContracts.value = ownProductContracts
        } catch (e) {
            ownAccommodationContracts.value = []
        }
    }

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const [_itinerary] = await app.$api.ownPackageContractsItinerary.get(props.contractId, {
                id: props.editItineraryId,
                locale: 'all',
            })
            itinerary.value = _itinerary
            if (!itinerary.value.arrivalAirportCode) itinerary.value.arrivalAirportCode = null
            if (!itinerary.value.departureAirportCode) itinerary.value.departureAirportCode = null
        } catch (e) {
            itinerary.value = initItinerary()
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const save = async () => {
        try {
            isSaving.value = true
            const data = clone(itinerary.value)
            clean(data)
            if (props.editItineraryId) {
                await app.$api.ownPackageContractsItinerary.put(props.contractId, props.editItineraryId, data)
            } else {
                await app.$api.ownPackageContractsItinerary.post(props.contractId, data)
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isSaving.value = false
        }
    }

    const useDays = computed(() => {
        return Array.from({length: duration.value}, (_, index) => index + 1)
    })

    const useNights = computed(() => {
        if (duration.value <= 1) return [0]
        return Array.from({length: duration.value - 1}, (_, index) => index + 1)
    })

    const saveSelectedPoints = points => {
        const {contracts, selectedPoints} = points
        itinerary.value.productIds = selectedPoints
        multipleProducts.value = contracts.filter(({id}) => selectedPoints.includes(id))
        isShowHotelModal.value = false
    }

    const useMethod = computed(() => {
        switch (itineraryType.value) {
            case 'ACCOMMODATION':
                return 'ownAccommodationContracts'
            case 'TRANSFER':
                return 'ownTransferContracts'
            case 'FLIGHT':
                return 'ownCharterContracts'
            case 'EXCURSION':
                return 'ownExcursionContracts'
            case 'INSURANCE':
                return 'ownInsuranceContracts'
            case 'EVENT':
                return 'ownEventContracts'
            case 'EXTRA_SERVICE':
                return 'ownExtraServiceContracts'
            case 'CAR':
                return 'ownCarRentContracts'
            case 'CRUISE':
                return 'ownCruiseContracts'
            default:
                return 'ownAccommodationContracts'
        }
    })

    const useIsShowMultipleTable = computed(() => {
        return multipleProducts.value.length
    })

    const resetProducts = () => {
        itinerary.value.productIds = []
        multipleProducts.value = []
    }

    const useHeaders = computed(() => {
        return [
            {title: t('hotels'), value: 'products'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
    })

    const useMultipleProducts = computed(() => {
        return [{products: multipleProducts.value.map(({name}) => name).join(', ')}]
    })
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(.v-autocomplete__selection) {
        height: fit-content;
    }
</style>
